import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLink } from "~/components/ui/AppLink";
import { Button } from "~/components/ui/Button";
import { Checkbox } from "~/components/ui/Checkbox";
import { Modal } from "~/components/ui/Modal";
import { useConsentStore } from "~/store/consentStore";
import {
  CONSENT_CHANGE_EVENT,
  buildConsentCookie,
  makeConsent,
  parseConsent,
  readRawConsent,
} from "~/lib/consent";

function CloseIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/**
 * The control that re-opens the preferences — the footer's «Preferencias de cookies» or
 * the profile's legal row (`.legal-cookies`). Focus lands there when the control that
 * opened the dialog has gone with the banner (`ESTADOS-Y-ESPECIFICACIONES.md § 1.4`).
 */
function focusPreferencesControl() {
  const active = document.activeElement;
  if (active && active !== document.body && document.contains(active)) return;
  // The visible one: a member's footer is hidden on a phone (`.foot.is-app-chrome`).
  const control = [...document.querySelectorAll<HTMLElement>(".legal-cookies")].find((el) => el.checkVisibility?.() ?? true);
  control?.focus({ preventScroll: true });
}

/**
 * T872 S9 — cookie consent on the October delivery (`components/CookiePreferences.html`,
 * the 2026-10-08 consolidation: compact banner, equal twins, preferences in their own
 * dialog). Two surfaces, one consent:
 *
 * - **Banner**, first visit only (no valid `tfc_consent`): non-blocking, a short text with
 *   the policy link, «Rechazar todas» and «Aceptar todas» as exact twins (R-T35-3), and
 *   «Preferencias» as a separate control.
 * - **Preferences dialog** (`Modal`: focus trap, Escape, focus back to the opener):
 *   essentials always on, analytics and marketing as boxes pre-filled from the stored
 *   consent, «Guardar preferencias» primary, «Rechazar opcionales» secondary. Closing
 *   without saving changes nothing; the banner, if it was up, stays.
 *
 * `useConsentStore.isOpen` is the dialog; the footer and the profile open it. Every choice
 * is written to the cookie and announced with `CONSENT_CHANGE_EVENT` (T78) — never held
 * only in memory.
 */
export function CookieBanner() {
  const { t } = useTranslation("consent");
  const prefsOpen = useConsentStore((s) => s.isOpen);
  const openPrefs = useConsentStore((s) => s.open);
  const closePrefs = useConsentStore((s) => s.close);

  const [banner, setBanner] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState("");
  /** A surface closed by her own act; focus follows it to the footer's control. */
  const closedByChoice = useRef(false);

  // First visit (no valid consent cookie) → the banner.
  useEffect(() => {
    if (!parseConsent(readRawConsent(document.cookie))) setBanner(true);
  }, []);

  // Each opening of the dialog starts from what is stored, so a cancelled edit never lingers.
  useEffect(() => {
    if (!prefsOpen) return;
    const existing = parseConsent(readRawConsent(document.cookie));
    setAnalytics(existing?.analytics ?? false);
    setMarketing(existing?.marketing ?? false);
    setStatus("");
  }, [prefsOpen]);

  // After the Modal has given the focus back (its cleanup runs first): if that control left
  // with the banner, the footer's preferences control takes it.
  useEffect(() => {
    if (prefsOpen || !closedByChoice.current) return;
    closedByChoice.current = false;
    focusPreferencesControl();
  }, [prefsOpen, banner]);

  const persist = (analyticsChoice: boolean, marketingChoice: boolean) => {
    const state = makeConsent({ analytics: analyticsChoice, marketing: marketingChoice });
    document.cookie = buildConsentCookie(state, {
      secure: typeof location !== "undefined" && location.protocol === "https:",
    });
    // T78 — tell AnalyticsProvider the cookie moved, so the choice takes effect without a
    // reload (the auto-test never navigates). No payload: the listener re-reads the cookie.
    window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
    closedByChoice.current = true;
    setBanner(false);
    closePrefs();
    setStatus(t("saved"));
  };

  return (
    <>
      {banner && (
        <div className="cc-dock">
          <section className="cc" aria-label={t("aria_label")}>
            <div>
              <p className="cc-eyebrow">{t("eyebrow")}</p>
              <p className="cc-text">
                {t("text")}{" "}
                <AppLink to="/legal/cookies" className="cc-policy">
                  {t("policy_link")}
                </AppLink>
                .
              </p>
            </div>
            <div className="cc-actions">
              {/* R-T35-3 — exact twins: one variant, one class, neither pre-selected. */}
              <Button type="button" variant="public-secondary" className="cc-twin" onClick={() => persist(false, false)}>
                {t("reject_all")}
              </Button>
              <Button type="button" variant="public-secondary" className="cc-twin" onClick={() => persist(true, true)}>
                {t("accept_all")}
              </Button>
              <Button type="button" variant="unstyled" className="cc-manage" aria-haspopup="dialog" onClick={openPrefs}>
                {t("manage")}
              </Button>
            </div>
          </section>
        </div>
      )}

      <Modal
        open={prefsOpen}
        onClose={closePrefs}
        ariaLabel={t("prefs_title")}
        className="cc-dialog"
        stackClassName="cc-dialog-stack"
        showCloseButton={false}
      >
        <Button type="button" variant="unstyled" className="cc-close" aria-label={t("close")} onClick={closePrefs}>
          <CloseIcon />
        </Button>
        <p className="cc-eyebrow">{t("eyebrow")}</p>
        <h2 className="cc-dialog-title">{t("prefs_title")}</h2>
        <p className="cc-text">{t("prefs_sub")}</p>
        <div className="cc-cats">
          <div className="cc-cat">
            <strong>{t("category.essential.title")}</strong>
            <span className="cc-cat-lock">{t("category.essential.lock")}</span>
            <p className="cc-cat-desc">{t("category.essential.description")}</p>
          </div>
          <Checkbox
            variant="public"
            id="cc-analytics"
            name="analytics"
            checked={analytics}
            onChange={(e) => setAnalytics(e.target.checked)}
            label={
              <>
                <strong>{t("category.analytics.title")}</strong>
                <span className="cc-cat-desc">{t("category.analytics.description")}</span>
              </>
            }
          />
          <Checkbox
            variant="public"
            id="cc-marketing"
            name="marketing"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            label={
              <>
                <strong>{t("category.marketing.title")}</strong>
                <span className="cc-cat-desc">{t("category.marketing.description")}</span>
              </>
            }
          />
        </div>
        <div className="cc-dialog-actions">
          <Button type="button" variant="public-primary" onClick={() => persist(analytics, marketing)}>
            {t("save")}
          </Button>
          <Button type="button" variant="public-secondary" onClick={() => persist(false, false)}>
            {t("reject_optional")}
          </Button>
        </div>
        {/* Her comprehension round (2026-10-11): three of five readers could not tell what the
            × does. It changes nothing; without a choice only the essentials run. */}
        <p className="cc-dialog-note">{t("close_note")}</p>
        <p className="cc-dialog-policy">
          <AppLink to="/legal/cookies" className="cc-policy" target="_blank" rel="noopener noreferrer">
            {t("policy_link")}
          </AppLink>
        </p>
      </Modal>

      {/* «Preferencias guardadas.» — mounted before it speaks, so the announcement is heard. */}
      <p className="sr-only" role="status">
        {status}
      </p>
    </>
  );
}
